'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { FlatQuotationLine, SmallLotPricing } from '@ravago/shared/page-data/models/elements';

import { resolveCombinedQuantity } from '../utils/quote-pricing/combined-pricing.utils';
import { getLastTierQuantityTo, sortTiers } from '../utils/quote-pricing/small-lot-tier.utils';

import { usePricingQuantityState } from './use-pricing-quantity-state';

export type PricingTab = 'quotes' | 'list-prices';

interface Params {
   quoteLines?: FlatQuotationLine[];
   smallLotPricing: SmallLotPricing | null;
}

/** Input to fall back to when there is no quote line: the last small-lot tier's upper bound. */
const getSmallLotFallbackInput = (smallLotPricing: SmallLotPricing | null): string => {
   if (!smallLotPricing || smallLotPricing.tiers.length === 0) return '';
   return (getLastTierQuantityTo(sortTiers(smallLotPricing.tiers)) || smallLotPricing.packaging.netWeight).toString();
};

/**
 * Owns the shared Amount input for the combined Quotes + List prices view (request for quotation).
 *
 * The input is the single source of truth: its resolved value drives which tab auto-activates
 * and what the summary + Add to cart reflect. Manual tab switches (`setActiveTab`) do NOT touch
 * the input.
 */
export const useCombinedPricingState = ({ quoteLines = [], smallLotPricing }: Params) => {
   const tiers = useMemo(() => smallLotPricing?.tiers ?? [], [smallLotPricing]);
   const netWeight = smallLotPricing?.packaging.netWeight;
   const hasSmallLot = tiers.length > 0 && netWeight !== undefined && netWeight > 0;

   const resolve = useCallback(
      (quantity: number, preferredQuoteLineId: string | undefined) =>
         resolveCombinedQuantity(quantity, { quoteLines, tiers, netWeight, preferredQuoteLineId }),
      [quoteLines, tiers, netWeight]
   );

   const { inputValue, onInputChange, hasSettled, resolution, selectQuantity, defaultLine } = usePricingQuantityState({
      quoteLines,
      fallbackInput: getSmallLotFallbackInput(smallLotPricing),
      resolve
   });

   const [activeTab, setActiveTab] = useState<PricingTab>(() => (defaultLine ? 'quotes' : 'list-prices'));

   // Auto-activate the tab that matches a valid resolution. Input-driven only:
   // a manual tab switch does not change `resolution`, so it is never overridden here.
   useEffect(() => {
      if (resolution.kind === 'quote') setActiveTab('quotes');
      else if (resolution.kind === 'small-lot') setActiveTab('list-prices');
   }, [resolution]);

   return {
      inputValue,
      onInputChange,
      hasSettled,
      resolution,
      activeTab,
      setActiveTab,
      selectQuantity,
      hasSmallLot,
      singleQuoteNoSmallLot: quoteLines.length === 1 && !hasSmallLot,
      netWeight,
      tiers
   };
};
